'use client'
// console.log("AuthContext module loaded");

import React, { createContext, useContext, useEffect, useState, useMemo, useRef } from 'react'
import { createClient } from '@/utils/supabase-browser'
import { useRouter } from 'next/navigation'
import type { User, AuthChangeEvent, Session } from '@supabase/supabase-js'
import { handleSupabaseError, type AppError } from '@/utils/error-handling'

// Define a type for the profile data we fetch
interface UserProfile {
  id: string;
  full_name: string | null;
  email: string | null;
  bio: string | null;
  // Add other profile fields if needed (e.g., avatar_url)
}

// Define type for the Access Level data
interface AccessLevel {
  id: string;
  name: string;
  description: string | null;
  page_access: string[] | ['*']; // Array of allowed paths or wildcard
  rls_permissions: { [tableName: string]: string[] | ['*'] }; // e.g., { "posts": ["select", "insert"], "*": [...] }
  brand_access: { type: 'all' | 'specific' | 'none', ids?: string[] }; // Brand access config
}

// Define type for combined User Profile and Access Level
interface UserAccess extends UserProfile {
  accessLevel: AccessLevel | null; // Include the full access level details
}

type AuthContextType = {
  user: User | null
  userAccess: UserAccess | null; // Renamed from profile to userAccess
  loading: boolean
  error: AppError | null
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{
    error: AppError | null
    data: { user: User | null } | null
  }>
  signUp: (email: string, password: string, name: string) => Promise<{
    error: AppError | null
    data: { user: User | null } | null
  }>
  signOut: () => Promise<void>
  refreshUserSession: () => Promise<void>
  clearError: () => void
  // --- Helper functions for checking permissions ---
  canAccessPage: (path: string) => boolean;
  canPerformAction: (tableName: string, action: 'select' | 'insert' | 'update' | 'delete') => boolean;
  getAllowedBrandIds: () => string[] | 'all' | 'none';
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [userAccess, setUserAccess] = useState<UserAccess | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<AppError | null>(null)
  const router = useRouter()
  
  // Add a debug log function to trace auth state
  const logAuthState = (message: string) => {
    // console.log(`[AUTH_DEBUG] ${message} | User: ${user?.id || 'null'} | Loading: ${loading} | Error: ${error ? 'yes' : 'no'}`);
  }
  
  // Log initial state
  useEffect(() => {
    // console.log('[AUTH_DEBUG] Initial auth state set', { 
    //   user: user ? { id: user.id } : null, 
    //   loading, 
    //   error: error ? error.message : null 
    // });
  }, [user, loading, error]);

  // ADDED: Ref to track currently processing user ID for SIGNED_IN
  const processingSignInUserIdRef = useRef<string | null>(null);
  const bypassKeepAliveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const authRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const tabVisibilityListenerRef = useRef<(() => void) | null>(null);
  const crossTabListenerRef = useRef<((event: StorageEvent) => void) | null>(null);
  const windowFocusListenerRef = useRef<(() => void) | null>(null);
  const supabase = useMemo(() => createClient(), []);

  const clearError = () => setError(null)
  
  // Production-safe logging function
  const prodLog = (message: string, data?: any) => {
    // Use console.error/warn which aren't stripped in production
    if (process.env.NODE_ENV === 'production') {
      console.warn(`[AUTH_PROD] ${message}`, data || '');
    } else {
      console.log(`[AUTH_DEV] ${message}`, data || '');
    }
  }

  // Handle tab visibility changes and cross-tab sync
  const handleTabVisibilityChange = async () => {
    if (document.hidden) {
      // Tab is now hidden - store current timestamp
      prodLog('Tab hidden, storing timestamp');
      localStorage.setItem('auth_tab_hidden_at', Date.now().toString());
    } else {
      // Tab is now visible - check if we need to refresh auth state
      prodLog('Tab visible, checking auth state', { hasUser: !!user, loading });
      
      // Check if auth state was lost (user is null but we have auth artifacts)
      const hasAuthId = localStorage.getItem('auth_user_id');
      const hasAuthState = document.cookie.includes('auth_state=authenticated');
      const hasActiveSession = localStorage.getItem('auth_session_active') === 'true';
      
      if (!user && (hasAuthId || hasAuthState || hasActiveSession)) {
        prodLog('Auth state lost on tab switch, forcing immediate refresh');
        await fetchAuthData(false);
        return;
      }
      
      const hiddenAt = localStorage.getItem('auth_tab_hidden_at');
      if (hiddenAt) {
        const hiddenDuration = Date.now() - parseInt(hiddenAt);
        const maxHiddenTime = 5 * 60 * 1000; // 5 minutes
        
        prodLog('Tab was hidden for', { durationMin: Math.round(hiddenDuration / 1000 / 60) });
        
        if (hiddenDuration > maxHiddenTime) {
          // Tab was hidden for too long, force refresh auth state
          prodLog('Tab hidden too long, forcing auth refresh');
          await fetchAuthData(false);
        } else {
          // Quick check to ensure auth state is still valid
          prodLog('Quick auth state validation');
          const cachedData = localStorage.getItem('auth_data_cache');
          if (cachedData) {
            try {
              const parsed = JSON.parse(cachedData);
              const age = Date.now() - parsed.timestamp;
              if (age > 60 * 60 * 1000) { // 1 hour
                prodLog('Cached auth data expired, refreshing');
                await fetchAuthData(false);
              }
            } catch (error) {
              prodLog('Invalid cached data, refreshing');
              await fetchAuthData(false);
            }
          } else if (user) {
            // We have a user but no cache, refresh
            prodLog('No cached data but user exists, refreshing');
            await fetchAuthData(false);
          }
        }
        localStorage.removeItem('auth_tab_hidden_at');
      }
    }
  };

  // Handle cross-tab storage changes
  const handleCrossTabStorageChange = (event: StorageEvent) => {
    if (!event.key) return;
    
    prodLog('Cross-tab storage change detected', { key: event.key, newValue: event.newValue });
    
    // React to auth-related storage changes from other tabs
    if (event.key === 'auth_user_id') {
      if (event.newValue && event.newValue !== user?.id) {
        // Another tab signed in with different user
        prodLog('Another tab signed in, syncing auth state');
        fetchAuthData(false);
      } else if (!event.newValue && user) {
        // Another tab signed out
        prodLog('Another tab signed out, clearing local auth state');
        setUser(null);
        setUserAccess(null);
        setLoading(false);
      }
    } else if (event.key === 'auth_data_cache' && event.newValue) {
      // Another tab updated cached auth data
      try {
        const newAuthData = JSON.parse(event.newValue);
        const age = Date.now() - newAuthData.timestamp;
        if (age < 5 * 60 * 1000 && newAuthData.userAccess) { // 5 minutes
          prodLog('Syncing fresh auth data from another tab');
          setUserAccess(newAuthData.userAccess);
        }
      } catch (error) {
        prodLog('Failed to parse cross-tab auth data');
      }
    }
  };

  // Fetch user, profile, and access level data with retry logic
  const fetchAuthData = async (setGlobalLoading: boolean = true, retryCount: number = 0) => {
    const maxRetries = 3;
    const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Exponential backoff, max 5s
    
    // logAuthState('fetchAuthData started');

    if (!supabase) {
      console.error('AuthContext: fetchAuthData failed - No Supabase client available.');
      setError(new Error('Authentication client not initialized') as AppError);
      setLoading(false); // Ensure we're not stuck in loading state
      return;
    }

    try {
      if (setGlobalLoading) {
        // logAuthState('fetchAuthData setting loading TRUE');
        setLoading(true);
      }

      // Check current session state first
      // logAuthState('fetchAuthData calling getSession');
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        console.error('AuthContext: Session fetch error in fetchAuthData:', sessionError);
        throw handleSupabaseError(sessionError);
      }

      if (session?.user) {
        // logAuthState(`fetchAuthData found session for user: ${session.user.id}`);
        // Auth has user. Update UI state & load authorized profile and permissions.
        const userId = session.user.id;
        setUser(session.user);

        // Fetch user profile from profiles table
        // logAuthState('fetchAuthData fetching profile data');
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (profileError) {
          console.warn('AuthContext: Profile fetch error in fetchAuthData, trying to continue:', profileError);
          // Don't throw here, continue trying to set up data
        }

        // --- DEBUG ONLY ---
        // console.log('[DEBUG] profileData response:', profileData);
        // --- END DEBUG ---

        // Fetch user's access level
        // logAuthState('fetchAuthData fetching access levels');
        const { data: accessLevelData, error: accessLevelError } = await supabase
          .from('user_access_levels')
          .select('access_levels(*)')
          .eq('user_id', userId)
          .maybeSingle();

        if (accessLevelError) {
          console.warn('AuthContext: Access level fetch error in fetchAuthData:', accessLevelError);
          // Don't throw here, continue but set minimal default access
        }

        // --- DEBUG ONLY ---
        // console.log('[DEBUG] accessLevelData response:', accessLevelData);
        // --- END DEBUG ---

        // Combine profile and access level data
        const fullUserProfile: UserAccess = {
          id: userId,
          // Use profile data if available, otherwise use Supabase auth user metadata
          full_name: 
            profileData?.full_name || 
            session.user.user_metadata?.full_name || 
            session.user.user_metadata?.name || null,
          email: 
            profileData?.email || 
            session.user.email || null,
          bio: profileData?.bio || null,
          // Avatar could be added here if needed
          // Add access level data if available
          accessLevel: accessLevelData?.access_levels || null
        };

        // Store full user profile with access level in state
        // logAuthState(`fetchAuthData setting complete userAccess object for ${userId}`);
        setUserAccess(fullUserProfile);

        // Persist auth status markers with enhanced storage
        if (typeof window !== 'undefined') {
          try {
            // Store critical auth data with timestamp
            const authData = {
              userId,
              timestamp: Date.now(),
              userAccess: fullUserProfile
            };
            localStorage.setItem('auth_user_id', userId);
            localStorage.setItem('auth_session_active', 'true');
            localStorage.setItem('auth_data_cache', JSON.stringify(authData));
            
            // Set persistent cookie with secure settings
            const isProduction = process.env.NODE_ENV === 'production';
            const cookieSettings = `auth_state=authenticated; path=/; max-age=604800; SameSite=Lax${isProduction ? '; Secure' : ''}`;
            document.cookie = cookieSettings;
            
            prodLog('Auth state persisted successfully');
            
            // Force service worker to bypass cache for auth-related requests in production
            if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
              navigator.serviceWorker.ready.then(registration => {
                if (registration.active) {
                  // Send message to service worker to exclude auth endpoints from caching
                  registration.active.postMessage({
                    type: 'BYPASS_AUTH_CACHE',
                    endpoints: ['/auth/', '/api/auth/', '/supabase/auth/']
                  });
                }
              }).catch(error => {
                console.warn('Service worker communication failed:', error);
              });
            }
            
            // Set up periodic refresh to keep auth state current
            if (authRefreshIntervalRef.current) {
              clearInterval(authRefreshIntervalRef.current);
            }
            
            authRefreshIntervalRef.current = setInterval(async () => {
              try {
                // Check if session is still valid and refresh if needed
                const { data: { session }, error } = await supabase.auth.getSession();
                if (error) {
                  console.warn('Session check failed during periodic refresh:', error);
                  return;
                }
                
                if (session?.user) {
                  // Update cache with fresh data
                  await fetchAuthData(false);
                  prodLog('Auth state refreshed in background');
                } else {
                  // Session expired, clear cached data
                  localStorage.removeItem('auth_data_cache');
                  localStorage.removeItem('auth_user_id');
                  localStorage.removeItem('auth_session_active');
                  document.cookie = 'auth_state=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
                }
              } catch (refreshError) {
                console.warn('Error during periodic auth refresh:', refreshError);
              }
            }, 15 * 60 * 1000); // Refresh every 15 minutes
          } catch (error) {
            console.warn('Failed to persist auth state:', error);
          }
        }

        return fullUserProfile; // Return the combined data in case caller needs it
      } else {
        // logAuthState('fetchAuthData found NO session');
        setUser(null); // Clear user if no session
        setUserAccess(null); // Clear access if no user
      }
    } catch (error) {
      console.error(`AuthContext: Error in fetchAuthData (attempt ${retryCount + 1}/${maxRetries + 1}):`, error);
      
      // Check if this is a network/temporary error that's worth retrying
      const isRetryableError = error instanceof Error && (
        error.message.includes('network') ||
        error.message.includes('timeout') ||
        error.message.includes('fetch') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError')
      );
      
      if (retryCount < maxRetries && isRetryableError) {
        // logAuthState(`fetchAuthData retrying in ${retryDelay}ms`);
        prodLog(`Auth data fetch failed, retrying in ${retryDelay}ms (attempt ${retryCount + 1}/${maxRetries})`);
        
        setTimeout(() => {
          fetchAuthData(setGlobalLoading, retryCount + 1);
        }, retryDelay);
        
        return; // Don't set error state or stop loading yet - we're retrying
      }
      
      // If we've exhausted retries or it's not a retryable error
      // logAuthState('fetchAuthData caught an error'); // Add log here
      setError(handleSupabaseError(error));
      setUser(null); // Ensure user is null on error
      setUserAccess(null);
    } finally {
      // Only set loading false if we're not retrying
      if (retryCount >= maxRetries || !error) {
        // logAuthState('fetchAuthData finally block reached, setting loading FALSE');
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Ensure this runs only on the client and if supabase client exists
    if (typeof window === 'undefined' || !supabase) {
      if (!supabase && typeof window !== 'undefined') {
        // If supabase is null client-side after init, means creation failed.
        // logAuthState('No Supabase client available, stopping loading');
        setLoading(false); // Ensure loading stops if client creation failed
      }
      return;
    }

    // logAuthState('Auth initialization useEffect running');

    // Check if we have a bypass flag
    const isAuthBypassed = localStorage.getItem('auth_bypass_active') === 'true' || 
                          window.forceDashboardRender === true;
    
    if (isAuthBypassed) {
      // logAuthState('AUTH BYPASS MODE DETECTED - Using mock authentication');
      // Create a mock user
      const mockUserId = localStorage.getItem('auth_user_id') || `mock-user-${Date.now()}`;
      localStorage.setItem('auth_user_id', mockUserId);
      
      // Set up mock user and access
      setUser({
        id: mockUserId,
        email: 'mock@example.com',
        role: 'authenticated',
        app_metadata: { provider: 'email' },
        user_metadata: { full_name: 'Bypass User' },
        aud: 'authenticated',
        created_at: new Date().toISOString()
      } as unknown as User);
      
      setUserAccess({
        id: mockUserId,
        full_name: 'Bypass User',
        email: 'mock@example.com',
        bio: null,
        accessLevel: {
          id: 'mock-access-level',
          name: 'Admin',
          description: 'Mock admin access for bypassed auth',
          page_access: ['*'],
          brand_access: { type: 'all' },
          rls_permissions: { '*': ['*'] }
        }
      });
      
      // Set loading to false since we're done
      setLoading(false);
      
      // Set up a keepalive ping
      bypassKeepAliveIntervalRef.current = setInterval(() => {
        // console.log('[AUTH_BYPASS] Sending keepalive ping');
      }, 60000);
      
      return () => {
        if (bypassKeepAliveIntervalRef.current) {
          clearInterval(bypassKeepAliveIntervalRef.current);
        }
      };
    }

    // Logic to check local storage for existing auth hints and restore cached data
    try {
      const hasUserID = localStorage.getItem('auth_user_id');
      const hasActiveSession = localStorage.getItem('auth_session_active') === 'true';
      const hasAuthState = document.cookie.includes('auth_state=authenticated');
      const cachedAuthData = localStorage.getItem('auth_data_cache');
      
      if (hasUserID && (hasActiveSession || hasAuthState)) {
        // logAuthState('Found auth artifacts in storage, enabling auth success flag');
        localStorage.setItem('force_auth_success', 'true');
        
        // Try to restore from cached auth data if available and recent
        if (cachedAuthData) {
          try {
            const parsedAuthData = JSON.parse(cachedAuthData);
            const now = Date.now();
            const cacheAge = now - (parsedAuthData.timestamp || 0);
            // Extend cache age in production to handle PWA scenarios
            const maxCacheAge = process.env.NODE_ENV === 'production' ? 60 * 60 * 1000 : 30 * 60 * 1000; // 1 hour prod, 30 min dev
            
            prodLog('Attempting cache restoration', { cacheAge: Math.round(cacheAge / 1000 / 60), maxCacheAgeMin: Math.round(maxCacheAge / 1000 / 60) });
            
            if (cacheAge < maxCacheAge && parsedAuthData.userAccess) {
              // logAuthState('Restoring auth data from cache');
              
              // Create a mock user object for immediate state restoration
              const mockUser: User = {
                id: parsedAuthData.userId,
                email: parsedAuthData.userAccess.email || 'cached@example.com',
                role: 'authenticated',
                app_metadata: { provider: 'email' },
                user_metadata: { 
                  full_name: parsedAuthData.userAccess.full_name || 'Cached User' 
                },
                aud: 'authenticated',
                created_at: new Date().toISOString()
              } as User;
              
              // Restore state immediately from cache
              setUser(mockUser);
              setUserAccess(parsedAuthData.userAccess);
              setLoading(false);
              
              prodLog('Auth state restored from cache successfully', { userId: parsedAuthData.userId });
              
              // Set flag to refresh data in background
              localStorage.setItem('auth_cache_restored', 'true');
              
              // In production, also force a immediate background refresh due to PWA potential issues
              if (process.env.NODE_ENV === 'production') {
                setTimeout(() => {
                  prodLog('Triggering immediate background refresh for production');
                  fetchAuthData(false, 0);
                }, 1000);
              }
            } else {
              prodLog('Cache expired or invalid', { cacheAgeMin: Math.round(cacheAge / 1000 / 60), hasUserAccess: !!parsedAuthData.userAccess });
            }
          } catch (cacheError) {
            console.warn('Failed to parse cached auth data:', cacheError);
            // Clear invalid cache data
            localStorage.removeItem('auth_data_cache');
          }
        }
      }
    } catch (storageError) {
      console.warn('Error accessing browser storage during init:', storageError);
    }

    // Set up tab visibility and cross-tab sync listeners
    if (typeof document !== 'undefined') {
      // Tab visibility change listener
      tabVisibilityListenerRef.current = handleTabVisibilityChange;
      document.addEventListener('visibilitychange', tabVisibilityListenerRef.current);
      
      // Also listen for window focus/blur as backup
      const handleWindowFocus = () => {
        prodLog('Window focused, validating auth state');
        handleTabVisibilityChange();
      };
      
      windowFocusListenerRef.current = handleWindowFocus;
      window.addEventListener('focus', handleWindowFocus);
      
      // Cross-tab storage sync listener
      crossTabListenerRef.current = handleCrossTabStorageChange;
      window.addEventListener('storage', crossTabListenerRef.current);
      
      prodLog('Tab visibility and cross-tab sync listeners set up');
    }

    // Flag to prevent listener from processing initial state if getSession already did
    let initialSessionChecked = false;
    let authTimerId: NodeJS.Timeout | null = null;

    // Set a timeout to ensure we don't get stuck in loading state
    authTimerId = setTimeout(() => {
      if (loading) {
        // logAuthState('AUTH TIMEOUT - Still in loading state after 5 seconds');
        setLoading(false);
      }
    }, 5000);

    // Immediately check the session state
    (async () => {
      // logAuthState('Performing initial getSession check');
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          // logAuthState('Error during initial getSession check');
          throw handleSupabaseError(sessionError);
        }

        if (session?.user) {
          // logAuthState(`Initial getSession found user: ${session.user.id}`);
          
          // Check if we restored from cache - if so, do background refresh
          const wasRestoredFromCache = localStorage.getItem('auth_cache_restored') === 'true';
          if (wasRestoredFromCache) {
            // logAuthState('Background refresh after cache restoration');
            prodLog('Background refresh after cache restoration', { userId: session.user.id });
            localStorage.removeItem('auth_cache_restored');
            await fetchAuthData(false); // Background refresh, don't show loading
          } else {
            prodLog('Normal initial auth data fetch', { userId: session.user.id });
            await fetchAuthData(true); // Normal initial load
          }
          
          initialSessionChecked = true; // Mark initial state as handled
        } else {
          // Check if we have cached data but no session - might need to sign out
          const cachedAuthData = localStorage.getItem('auth_data_cache');
          if (cachedAuthData && user) {
            // logAuthState('Have cached data but no session - possible expired session');
            // Clear cached data and sign out
            localStorage.removeItem('auth_data_cache');
            localStorage.removeItem('auth_user_id');
            localStorage.removeItem('auth_session_active');
            document.cookie = 'auth_state=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
            setUser(null);
            setUserAccess(null);
          }
          
          // logAuthState('Initial getSession found no user, stopping loading');
          setLoading(false); // No user, no need to load further initially
          initialSessionChecked = true; // Mark initial state as handled (no session)
        }
      } catch (error) {
        console.error('Error during initial getSession check:', error);
        setError(handleSupabaseError(error));
        setLoading(false); // Stop loading on error during initial check
        initialSessionChecked = true; // Mark initial state as handled (error)
      }

      // --- Setup listener AFTER initial check ---
      // logAuthState('Setting up onAuthStateChange listener');
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event: AuthChangeEvent, session: Session | null) => {
          // logAuthState(`onAuthStateChange triggered: ${event}`); // Log event type
          setError(null); // Clear previous errors on any auth change

          switch (event) {
            case 'INITIAL_SESSION':
              // Handled by initial fetchAuthData call outside listener
              // logAuthState('INITIAL_SESSION event (usually handled by initial fetch)');
              break;
            case 'SIGNED_IN':
              // logAuthState(`SIGNED_IN event for user: ${session?.user?.id}`);
              if (session?.user && processingSignInUserIdRef.current !== session.user.id) {
                processingSignInUserIdRef.current = session.user.id; // Mark as processing
                // logAuthState('SIGNED_IN - Calling fetchAuthData(false)');
                try {
                  const fetchedAccess = await fetchAuthData(false);
                  // Explicitly set user/access AFTER fetch completes for SIGNED_IN
                  setUser(session.user);
                  setUserAccess(fetchedAccess || null);
                  // logAuthState('SIGNED_IN - User and Access set after fetch');

                  // *** ADDED: Redirect to dashboard after successful sign-in and data fetch ***
                  // logAuthState('SIGNED_IN - Redirecting to /dashboard');
                  router.push('/dashboard'); 

                } catch (signInError) {
                   console.error("Error fetching data on SIGNED_IN:", signInError);
                   // logAuthState('SIGNED_IN - Error during fetchAuthData');
                   // setError is handled within fetchAuthData
                } finally {
                    processingSignInUserIdRef.current = null; // Unmark processing
                    // logAuthState('SIGNED_IN - Finished processing');
                }
              }
              break;
            case 'SIGNED_OUT':
              // logAuthState('SIGNED_OUT event');
              setUser(null);
              setUserAccess(null);
              if (typeof window !== 'undefined') {
                localStorage.removeItem('auth_user_id');
                localStorage.removeItem('auth_session_active');
                localStorage.removeItem('auth_data_cache');
                localStorage.removeItem('auth_cache_restored');
                localStorage.removeItem('auth_tab_hidden_at');
                document.cookie = 'auth_state=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
                // logAuthState('SIGNED_OUT - Cleared local storage and cookie');
              }
              setLoading(false); // Explicitly stop loading on sign out
              // logAuthState('SIGNED_OUT - Navigating to /signin');
              router.push('/signin'); // Redirect to sign-in page
              break;
            case 'TOKEN_REFRESHED':
              // logAuthState(`TOKEN_REFRESHED event. Session user: ${session?.user?.id}`);
              if (session?.user) {
                // Re-fetch user data in case roles/permissions changed, but don't trigger global loading indicator
                // logAuthState('TOKEN_REFRESHED - Calling fetchAuthData(false)');
                try {
                  const refreshedAccess = await fetchAuthData(false);
                  // Update state if fetch was successful
                  setUser(session.user);
                  setUserAccess(refreshedAccess || null);
                  // logAuthState('TOKEN_REFRESHED - User and Access updated');
                } catch (refreshError) {
                  console.error("Error during fetchAuthData on TOKEN_REFRESHED:", refreshError);
                  // logAuthState('TOKEN_REFRESHED - Error during fetchAuthData');
                  // setError is handled within fetchAuthData
                  // Consider if we need to sign out the user here if refresh fails badly?
                }
              } else {
                // If token refresh results in no session, treat as sign out
                // logAuthState('TOKEN_REFRESHED - No session found, treating as SIGNED_OUT');
                setUser(null);
                setUserAccess(null);
                if (typeof window !== 'undefined') {
                   localStorage.removeItem('auth_user_id');
                   localStorage.removeItem('auth_session_active');
                   localStorage.removeItem('auth_data_cache');
                   localStorage.removeItem('auth_cache_restored');
                   localStorage.removeItem('auth_tab_hidden_at');
                   document.cookie = 'auth_state=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
                }
                setLoading(false);
                router.push('/signin');
              }
              break;
            case 'USER_UPDATED':
              // logAuthState(`USER_UPDATED event. Session user: ${session?.user?.id}`);
              if (session?.user) {
                // logAuthState('USER_UPDATED - Calling fetchAuthData(false)');
                try {
                    const updatedAccess = await fetchAuthData(false);
                    setUser(session.user); // Update user object from session
                    setUserAccess(updatedAccess || null); // Refresh access details
                    // logAuthState('USER_UPDATED - User and Access updated');
                } catch (updateError) {
                    console.error("Error during fetchAuthData on USER_UPDATED:", updateError);
                    // logAuthState('USER_UPDATED - Error during fetchAuthData');
                }
              }
              break;
            case 'PASSWORD_RECOVERY':
              // logAuthState('PASSWORD_RECOVERY event');
              // Usually handled by specific UI flow, maybe just log here
              // Potentially fetch data if a session is now active
              if (session?.user) {
                 // logAuthState('PASSWORD_RECOVERY - User session exists, calling fetchAuthData(false)');
                 await fetchAuthData(false);
              }
              break;
            default:
              // logAuthState(`Unhandled auth event: ${event}`);
          }
        }
      )

      // Return cleanup function
      return () => {
        // logAuthState('Auth useEffect cleanup');
        subscription.unsubscribe();
        if (authTimerId) clearTimeout(authTimerId);
        if (authRefreshIntervalRef.current) {
          clearInterval(authRefreshIntervalRef.current);
        }
        if (bypassKeepAliveIntervalRef.current) {
          clearInterval(bypassKeepAliveIntervalRef.current);
        }
        
        // Clean up tab visibility and cross-tab listeners
        if (typeof document !== 'undefined' && tabVisibilityListenerRef.current) {
          document.removeEventListener('visibilitychange', tabVisibilityListenerRef.current);
        }
        if (typeof window !== 'undefined') {
          if (crossTabListenerRef.current) {
            window.removeEventListener('storage', crossTabListenerRef.current);
          }
          if (windowFocusListenerRef.current) {
            window.removeEventListener('focus', windowFocusListenerRef.current);
          }
        }
      };
    })();
  }, []);

  // Function to manually refresh user and access level data
  const refreshUserSession = async () => {
    if (!supabase) {
      console.error('Cannot refresh user session: Authentication client not initialized');
      return;
    }
    await fetchAuthData(true); // Use TRUE for manual refresh (user initiated)
  };

  const signIn = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      clearError();
      // console.log('AuthContext: Attempting signIn');
      
      // Ensure we have a Supabase client
      if (!supabase) {
        throw new Error('Authentication client not initialized');
      }
      
      // Clear any existing auth flags first to ensure clean state
      if (typeof window !== 'undefined') {
        // Clear localStorage auth flags
        localStorage.removeItem('auth_in_progress');
        localStorage.removeItem('force_auth_success');
        localStorage.removeItem('auth_user_id');
        localStorage.removeItem('auth_session_active');
        
        // Clear sessionStorage auth flags
        sessionStorage.removeItem('force_dashboard_view');
        sessionStorage.removeItem('auth_redirect_count');
        sessionStorage.removeItem('signin_redirect_count');
        
        // Clear all auth-related cookies
        document.cookie = 'auth_state=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        
        // Now set new flags
        localStorage.setItem('rememberMe', rememberMe.toString());
        localStorage.setItem('auth_in_progress', 'true');
        sessionStorage.setItem('force_dashboard_view', 'true');
        localStorage.setItem('force_auth_success', 'true');
      }
      
      // Use Supabase signInWithPassword with explicit session handling
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('AuthContext: Sign-in error', error);
        if (typeof window !== 'undefined') {
          localStorage.removeItem('auth_in_progress');
          sessionStorage.removeItem('force_dashboard_view');
          localStorage.removeItem('force_auth_success');
        }
        throw handleSupabaseError(error);
      }

      // If we get here, the sign-in was successful
      // console.log('AuthContext: Sign-in successful, user data:', data.user?.id);
      
      // Explicitly set loading state while we fetch profile data
      // setLoading(true); // REMOVED - fetchAuthData will handle this now
      
      // Set user data immediately to avoid race conditions with redirection
      if (data.user) {
        // Store auth data in localStorage for redundancy
        if (typeof window !== 'undefined') {
          localStorage.setItem('auth_user_id', data.user.id);
          localStorage.setItem('auth_session_active', 'true');
          
          // Set a cookie with a longer expiration for session recovery
          const oneHourFromNow = new Date(new Date().getTime() + 60 * 60 * 1000);
          document.cookie = `auth_state=authenticated; path=/; expires=${oneHourFromNow.toUTCString()}; SameSite=Lax`;
          document.cookie = `auth_user_id=${data.user.id}; path=/; expires=${oneHourFromNow.toUTCString()}; SameSite=Lax`;
        }
        
        setUser(data.user);
        
        // Also attempt to fetch profile right away to ensure it's available
        try {
          // console.log('AuthContext: Post-signin, fetching auth data...');
          await fetchAuthData(true); // Use TRUE for explicit sign-in
          // console.log('AuthContext: Auth data fetched after sign-in.');
        } catch (authDataError) {
            // Log the error but don't necessarily block sign-in,
            // as the core auth succeeded. The main fetchAuthData handles errors.
            console.error('Error fetching auth data immediately after sign-in:', authDataError);
            // We might want to set an error state here or handle it more gracefully
            // For now, we let it proceed, but the userAccess state might be null.
        } finally {
            // setLoading(false); // REMOVED - fetchAuthData handles this
            // console.log('AuthContext: Loading state set to false after fetchAuthData attempt.');
        }
        
        // Check if this is a direct dashboard login
        const isDirectDashboardLogin = localStorage.getItem('direct_dashboard_login') === 'true';
        
        // REMOVED: Redirect logic is now handled by onAuthStateChange listener
        /*
        if (!isDirectDashboardLogin) {
          // Normal login flow with redirect
          // Check for redirect URL from local storage or query param
          const urlParams = new URLSearchParams(window.location.search);
          const redirectPath = urlParams.get('redirect') || '/dashboard';
          
          // Redirect to dashboard after a small delay to allow state to settle
          setTimeout(() => {
            if (typeof window !== 'undefined') {
              window.location.href = redirectPath;
            }
          }, 500); 
        } else {
          // Direct dashboard login - no redirect needed
          // console.log('Direct dashboard login successful');
          localStorage.removeItem('direct_dashboard_login');
        }
        */
      } else {
          // If somehow data.user is null after successful sign-in, set loading false
          setLoading(false); // Keep this failsafe
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('AuthContext: Error caught in signIn', error);
      const appError = handleSupabaseError(error);
      setError(appError);
      // setLoading(false); // Ensure loading is false on error - fetchAuthData handles this if called, otherwise need it here
      // Let's ensure loading is false if sign-in fails *before* fetchAuthData is called
      if (loading) setLoading(false); 
      return { data: null, error: appError };
    }
  }

  const signUp = async (email: string, password: string, name: string) => {
    try {
      clearError();
      
      // Ensure we have a Supabase client
      if (!supabase) {
        throw new Error('Authentication client not initialized');
      }
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name,
          },
        },
      });

      if (error) {
        throw handleSupabaseError(error);
      }

      return { data, error: null };
    } catch (error) {
      const appError = handleSupabaseError(error);
      setError(appError);
      return { data: null, error: appError };
    }
  }

  const signOut = async () => {
    try {
      clearError();
      // console.log('Signing out user...');
      const localSupabase = supabase; // Capture client instance

      // Clear local state immediately
      setUser(null);
      setUserAccess(null);
      setLoading(false); // Explicitly set loading false

      // Clear intervals before clearing storage
      if (authRefreshIntervalRef.current) {
        clearInterval(authRefreshIntervalRef.current);
        authRefreshIntervalRef.current = null;
      }
      if (bypassKeepAliveIntervalRef.current) {
        clearInterval(bypassKeepAliveIntervalRef.current);
        bypassKeepAliveIntervalRef.current = null;
      }

      // Clear all client-side auth state first
      if (typeof window !== 'undefined') {
        // Clear localStorage
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('auth_in_progress');
        localStorage.removeItem('auth_user_id');
        localStorage.removeItem('auth_session_active');
        localStorage.removeItem('auth_data_cache');
        localStorage.removeItem('auth_cache_restored');
        localStorage.removeItem('auth_tab_hidden_at');
        localStorage.removeItem('force_auth_success');
        localStorage.removeItem('supabase.auth.token');
        
        // Clear sessionStorage
        sessionStorage.removeItem('force_dashboard_view');
        sessionStorage.removeItem('auth_bypass');
        sessionStorage.removeItem('auth_redirecting');

        // Clear cookies - use multiple approaches for better browser compatibility
        document.cookie = 'auth_state=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
        document.cookie = 'auth_user_id=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
        document.cookie = 'force_auth_success=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
        document.cookie = 'force_dashboard_view=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
        document.cookie = 'auth_redirect_path=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
        document.cookie = 'supabase-auth-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
        
        // Clear any Supabase cookies
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf('=');
          const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
          if (name.startsWith('sb-')) {
            document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
          }
        }
      }
      
      // Now sign out from Supabase (even if this fails, we've cleared client-side state)
      if (localSupabase) {
         // console.log('AuthContext: Calling Supabase signOut...');
         const { error: signOutError } = await localSupabase.auth.signOut();
         if (signOutError) {
            // Log the error but proceed with redirection anyway
            console.error('AuthContext: Supabase signOut error:', signOutError);
         } else {
             // console.log('AuthContext: Supabase signOut successful.');
         }
      } else {
          console.warn('AuthContext: Supabase client not available during signOut.');
      }

      // console.log('Sign out completed, redirecting to home page');

      // Use direct window location change for most reliable redirect
      // Ensure this happens *after* state updates and Supabase call attempt
      window.location.href = '/';
    } catch (error) {
      console.error('Error during sign out:', error);
      setError(handleSupabaseError(error)); // Pass only the error object
      // Attempt to clear state again just in case
      setUser(null);
      setUserAccess(null);
      setLoading(false);
      // Even if there's an error, try to redirect to home
      try {
        window.location.href = '/';
      } catch (redirectError) {
        console.error('Could not redirect after sign out error:', redirectError);
      }
    }
  };

  // --- Helper functions for checking permissions ---

  const canAccessPage = (path: string): boolean => {
    // console.log(`[AuthContext] Checking page access for path: ${path}`);

    // 1. Handle loading or error states - Deny access if critical data is missing
    if (loading) {
        // console.log('[AuthContext] Access denied: Auth context still loading.');
        return false; // Or maybe return a specific loading state indicator?
    }
    if (error) {
        // console.log('[AuthContext] Access denied: Auth context has error:', error);
        return false; // Deny access if there was an error loading auth data
    }
    if (!user) {
        // console.log('[AuthContext] Access denied: No authenticated user.');
        return false; // Should be handled by redirects usually, but belt-and-suspenders
    }
    if (!userAccess) {
        // console.log('[AuthContext] Access denied: userAccess data not available.');
        // This might happen briefly between user being set and profile/access level loading
        // Or if profile/access level fetch failed.
        return false;
    }
    if (!userAccess.accessLevel) {
        console.warn('[AuthContext] Access denied: User has no assigned access level. Defaulting to deny.');
        // This case should ideally be prevented by assigning a default role upon profile creation.
        return false; // Deny access if no access level is defined
    }

    // 2. Check the access level permissions
    const { page_access } = userAccess.accessLevel;
    // console.log(`[AuthContext] User page_access permissions:`, page_access);

    // Allow access if page_access includes the wildcard '*'
    if (page_access.includes('*')) {
        // console.log('[AuthContext] Access granted: Wildcard (*) permission found.');
        return true;
    }

    // Normalize the input path (remove trailing slash if present, ensure leading slash)
    const normalizedPath = path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
    const requiredPath = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;

    // Check if the normalized path exists in the page_access array
    // We should also handle potential variations like base paths vs full paths.
    // Example: if page_access has '/dashboard', should '/dashboard/settings' be allowed?
    // For now, we do an exact match or prefix match.
    const hasAccess = page_access.some(allowedPath => {
        const normalizedAllowedPath = allowedPath.endsWith('/') && allowedPath.length > 1 ? allowedPath.slice(0, -1) : allowedPath;
        const finalAllowedPath = normalizedAllowedPath.startsWith('/') ? normalizedAllowedPath : `/${normalizedAllowedPath}`;
        
        // Check for exact match or if the allowed path is a prefix of the required path,
        // accommodating for sub-paths (starting with '/') or query parameters (starting with '?')
        if (requiredPath === finalAllowedPath) {
            return true;
        }
        if (requiredPath.startsWith(finalAllowedPath)) {
            const nextChar = requiredPath.charAt(finalAllowedPath.length);
            return nextChar === '/' || nextChar === '?';
        }
        return false;
    });

    if (hasAccess) {
        // console.log(`[AuthContext] Access granted: Path ${requiredPath} is allowed.`);
    } else {
        // console.log(`[AuthContext] Access denied: Path ${requiredPath} not found in allowed paths.`);
    }

    return hasAccess;
  };

  const canPerformAction = (tableName: string, action: 'select' | 'insert' | 'update' | 'delete'): boolean => {
    if (loading) return false;
    if (!userAccess?.accessLevel?.rls_permissions) return false;

    const permissions = userAccess.accessLevel.rls_permissions;
    const tablePermissions = permissions[tableName] ?? permissions['*']; // Check specific table, then wildcard '*'

    if (!tablePermissions) return false; // No permissions defined for this table or wildcard

    // Check for wildcard action first
    if (tablePermissions.includes('*')) {
        return true;
    }

    // If not wildcard, check for the specific action.
    // We assert tablePermissions is string[] here because the wildcard case was handled.
    return (tablePermissions as string[]).includes(action);
  };

  const getAllowedBrandIds = (): string[] | 'all' | 'none' => {
    if (loading || !userAccess?.accessLevel?.brand_access) return 'none'; // Default to 'none' if loading or no config

    const brandAccess = userAccess.accessLevel.brand_access;

    if (brandAccess.type === 'all') return 'all';
    if (brandAccess.type === 'specific' && Array.isArray(brandAccess.ids)) {
        return brandAccess.ids;
    }
    return 'none'; // Default to none
  };

  const value = {
    user,
    userAccess, // Use userAccess here
    loading,
    error,
    signIn,
    signUp,
    signOut,
    refreshUserSession,
    clearError,
    // Include helper functions
    canAccessPage,
    canPerformAction,
    getAllowedBrandIds,
  }

  // Memoize the context value
  const memoizedValue = useMemo(() => ({
    user,
    userAccess, // Use userAccess here
    loading,
    error,
    signIn,
    signUp,
    signOut,
    refreshUserSession,
    clearError,
    // Include helper functions
    canAccessPage,
    canPerformAction,
    getAllowedBrandIds,
  }), [user, userAccess, loading, error]); // Update dependencies

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => { // Ensure hook returns the updated type
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}